import { useEffect, useRef, useState, type FormEvent } from "react";
import type { ResizeConfig, ResizeResult } from "../hooks/useAdminClaws";
import type { AdminOpenClaw } from "../types/openclaw";
import styles from "./ReassignModal.module.css";

type Props = {
  open: boolean;
  claw: AdminOpenClaw | null;
  onClose: () => void;
  fetchConfig: () => Promise<ResizeConfig | null>;
  onSubmit: (tenantId: string, instanceType: string) => Promise<ResizeResult>;
};

// The backend GET /resize-config is the source of truth for which types are
// offered; this is only the resilience fallback if that fetch fails, so the
// modal stays usable. Keep it in sync with ztc.resize.allowed-types defaults.
const FALLBACK_CONFIG: ResizeConfig = {
  allowedTypes: ["t3a.small", "t3a.medium", "t3a.large"],
  upDefault: "t3a.medium",
  downDefault: "t3a.small",
};

// Human labels for known types; unknown types render as their raw id so a
// backend-added size still shows up (just without a friendly label).
const SIZE_LABELS: Record<string, string> = {
  "t3a.small": "t3a.small — 2 vCPU / 2 GB",
  "t3a.medium": "t3a.medium — 2 vCPU / 4 GB",
  "t3a.large": "t3a.large — 2 vCPU / 8 GB",
};
const labelFor = (type: string): string => SIZE_LABELS[type] ?? type;

// Sensible initial selection: one step away from where the box is now, so
// the default never proposes a no-op resize. Falls back to the down default
// when the current type is unknown or not in the allowlist.
const initialTarget = (cfg: ResizeConfig, current: string | null | undefined): string => {
  const idx = current ? cfg.allowedTypes.indexOf(current) : -1;
  if (idx < 0) return cfg.downDefault;
  const step = cfg.allowedTypes[idx - 1] ?? cfg.allowedTypes[idx + 1];
  return step ?? cfg.downDefault;
};

const hintFor = (cfg: ResizeConfig, current: string | null | undefined, target: string): string => {
  if (current === target) return "Already this size — the resize will be a no-op.";
  const from = current ? cfg.allowedTypes.indexOf(current) : -1;
  const to = cfg.allowedTypes.indexOf(target);
  if (from >= 0 && to > from) return "Larger instance — roughly double the compute cost per step up.";
  if (from >= 0 && to < from) return "Smaller instance — watch for memory pressure / OOM under load.";
  return target === cfg.upDefault ? "Full-size baseline." : "";
};

export const ResizeInstanceModal = ({ open, claw, onClose, fetchConfig, onSubmit }: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [config, setConfig] = useState<ResizeConfig>(FALLBACK_CONFIG);
  const [target, setTarget] = useState<string>(FALLBACK_CONFIG.downDefault);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && claw !== null && !d.open) {
      d.showModal();
      setServerError(null);
      setSubmitting(false);
      void fetchConfig().then((cfg) => {
        const next = cfg && cfg.allowedTypes.length > 0 ? cfg : FALLBACK_CONFIG;
        setConfig(next);
        setTarget(initialTarget(next, claw.instanceType));
      });
    } else if (!open && d.open) {
      d.close();
    }
  }, [open, claw, fetchConfig]);

  if (!open || claw === null) return null;

  const handleSubmit = async (evt: FormEvent): Promise<void> => {
    evt.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(claw.tenantId, target);
    setSubmitting(false);
    if (result.ok) {
      onClose();
      return;
    }
    setServerError(result.message);
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) onClose();
      }}
    >
      <form method="dialog" className={styles.form} onSubmit={handleSubmit}>
        <header className={styles.header}>
          <h2 className={styles.title}>Resize instance</h2>
          <p className={styles.subtitle}>
            Change the EC2 instance type for{" "}
            <span className={styles.nameToken}>{claw.name}</span>. This stops,
            re-types, and restarts the box — expect a 1–5 min interruption while
            it reboots and channels reconnect.
          </p>
        </header>

        <label className={styles.field}>
          <span className={styles.label}>Target instance type</span>
          <select
            className={styles.input}
            value={target}
            onChange={(e) => { setTarget(e.target.value); setServerError(null); }}
            disabled={submitting}
          >
            {config.allowedTypes.map((t) => (
              <option key={t} value={t}>{labelFor(t)}</option>
            ))}
          </select>
          <span className={styles.hint}>
            {claw.instanceType ? `Currently ${claw.instanceType}. ` : ""}
            {hintFor(config, claw.instanceType, target)}
          </span>
        </label>

        {serverError !== null ? (
          <div className={styles.errorBanner}>{serverError}</div>
        ) : null}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={submitting}>
            {submitting ? "Resizing…" : "Resize"}
          </button>
        </footer>
      </form>
    </dialog>
  );
};
